<template>
  <main>
    <!-- Skeleton -->
    <div v-if="pending && (!data || !articleData?.numFound)" class="mb-8 mt-10 f-col items-center space-y-3 container">
      <div class="h-5 w-53 skeleton lg:h-6 lg:w-61 md:h-4" />
      <div class="skeleton lg:h-4 lg:w-61" />
    </div>
    <template v-else>
      <cms-section v-if="aboveGridESpots" :key="numResults" class="my-4 md:mb-8 md:mt-6" :section="aboveGridESpots" />
      <div
        class="my-2 text-center space-y-1 container "
        :class="{ 'mb-2 lg:mb-6 md:mb-10': !data?.products.length }"
      >
        <h1 v-if="isArticlesPage" aria-live="polite" class="title-2">
          {{ articlesTitle }}
        </h1>
        <template v-else>
          <vf-notification v-if="error" class="mb-4" :dismissible="false" type="error">
            {{ error.message }}
          </vf-notification>
          <h1 v-if="!hideTitle" aria-live="polite" class="title-2">
            {{ productsTitle }}
          </h1>
          <form
            v-if="showSearch"
            :class="{ 'my-2': noResultMessage }"
            role="search"
            @submit.prevent="submit"
          >
            <div v-style:w="{ md: '36rem' }" class="m-a bg-grey-90 flex relative">
              <base-button
                :aria-label="$t.searchButtonLabel"
                class="ml-3"
                :class="brandClasses.button"
                type="submit"
              >
                <vf-icon name="search" :size="iconSize" />
              </base-button>
              <vf-input class="b-0 grow h-12 !bg-transparent">
                <template #input>
                  <base-input
                    ref="searchFieldRef"
                    v-model.trim="searchFieldQuery"
                    aria-labelledby="vf-search-form-input"
                    class="focus-visible:outline-none h-full"
                    :placeholder="$t.searchPlaceholder"
                    type="text"
                  />
                </template>
                <template #end>
                  <base-button
                    v-if="searchFieldQuery"
                    :aria-label="$t.searchClearLabel"
                    @click="clear()"
                  >
                    <vf-icon name="close" size="md" />
                  </base-button>
                </template>
              </vf-input>
            </div>
          </form>
          <!-- Did you mean or auto-correct query message -->
          <p v-if="noResultMessage" >
            <base-interpolator :content="noResultMessage">
              <template #query>
                <span class="fw-medium">‘{{ data?.autoCorrectQuery }}’</span>
              </template>
              <template #initialQuery>
                <span class="fw-medium">‘{{ query.q }}’</span>
              </template>
              <template #links>
                <vf-link class="subtitle-5" :to="`/search?q=${data?.did_you_mean?.[0]}`" variant="quiet">
                  ‘{{ data?.did_you_mean?.[0] }}’
                </vf-link>
                <span class="text-sm">?</span>
              </template>
            </base-interpolator>
          </p>
          <div v-if="data?.campaign" v-html="data.campaign.htmlText" />
        </template>
      </div>
    </template>
    <!-- Skeleton -->
    <template v-if="pending">
      <div class="mb-4 flex justify-center space-x-3 container">
        <div class="h-6 w-29 skeleton" />
        <div class="h-6 w-29 skeleton" />
      </div>
    </template>
    <div
      v-else-if="$feature.showArticlesTabOnSRP && data?.products.length"
      class="mb-2 b-b b-grey-70 flex w-full justify-center container "
    >
      <base-link
        class="text-sm px-4 py-1 b-b-4 b-transparent relative "
        exact-active-class="!b-brand-2 "
        :to="{ path: '/search', query: { q: searchQuery } }"
      >
        {{ $t.products.other }} <template v-if="data?.total !== undefined">
          ({{ data.total }})
        </template>
      </base-link>

      <base-link
        class="text-sm px-4 py-1 b-b-4 b-transparent relative "
        exact-active-class="!b-brand-2 "
        :to="{ path: '/search/articles', query: { q: searchQuery } }"
      >
        {{ $t.articles.other }} <template v-if="articleData?.numFound !== undefined">
          ({{ articleData.numFound }})
        </template>
      </base-link>
    </div>
    <div v-if="$feature.showFitFinderOnSRP" class="container">
      <div id="fit-finder-srp" />
    </div>
    <NuxtPage
      v-bind="{
        infinityLoad,
        pending,
        searchQuery: query.q,
        sideFilters: $feature.showSideFiltersOnSRP,
        trackingCategory,
      }"
      :product-data="data ? { ...data, title: productsTitle } : null"
      @load-more="loadMore"
    />
    <cms-section
      v-if="!pending && aboveFooterESpots"
      :key="numResults"
      class="my-4 md:mb-8 md:mt-6 "
      lazy-media
      :section="aboveFooterESpots"
    />
  </main>
</template>

<script lang="ts" setup>
import type { BaseInput as BaseInputType } from '#components'
import type { ProductSearchQuery } from '#types/product'

defineOptions({ name: 'ProductSearchPage' })
definePageMeta({
  key: ({ query: { q } }) => `srp-${q}`,
  middleware: 'plp-store-filter',
  keepalive: true
})

const { iconSize, brandClasses } = useAppConfig().components.vf.search
const { $dtrum, $t, $pluralize, $feature, $gtm, $viewport } = useNuxtApp()
const { defaultSort, hideDidYouMean, hideSubtitle, hideTitle } = useAppConfig().pages.plp
const { productsPerPage, grid } = useAppConfig().components.product.list
const { defaultSearchTerm } = useAppConfig().components.vf.search || {}
const { showSearch } = useAppConfig().pages.search || {}
const { injectFitFinder } = useFitFinder()
const { getSections } = useCms()
const route = useRoute()
const router = useRouter()
const sort = useRouteQuery('sort', defaultSort)
const storeFilter = $feature.showPickupFiltersOnSRP ? useRouteQuery<string | undefined>('storeFilter') : undefined
const searchQuery = useRouteQuery('q', '')
const filters = useRouteQuery<string>('filters', '')
const filtersQuery = computed(() => getFiltersQueryString(filters.value))
const query = reactive<ProductSearchQuery>({
  start: 0,
  count: productsPerPage,
  q: searchQuery.value || defaultSearchTerm || '',
  sort: sort.value,
  filters: filtersQuery.value,
  storeFilter: toValue(storeFilter),
  ...(route.query.customTrigger ? { customTrigger: route.query.customTrigger as string } : {})
})
const { trackEvent } = await useOptimizely()

const activeTags = useRouteQuery<string[]>('tags', [], { transform: (param) => [param].flat() })
const cart = useCartStore()
const infinityLoad = ref(false)
const { addSearch } = useSearchHistory()
const searchFieldRef = ref<InstanceType<typeof BaseInputType>>()

const { data: articleData, pending: loading } = useAsyncData(`article-count-${searchQuery.value}`, () => useCms().getSearch('srp', {
  q: searchQuery.value,
  tagId: activeTags.value.join(','),
  countOnly: true
}), { lazy: true, watch: [activeTags], server: false })

const searchFieldQuery = ref(searchQuery.value || '')

const submit = () => {
  if (searchFieldQuery.value.trim() && searchFieldQuery.value !== query.q)
    router.push({ query: { ...route.query, q: searchFieldQuery.value } })
}

const { data, pending: productLoading, error } = useApi().products.search(query, {
  server: false,
  key: searchQuery.value,
  transform: (input): typeof input => ({
    ...input,
    did_you_mean: input.did_you_mean?.filter(Boolean) || [],
    filters: getUrlSafeFilters(input.filters),
    products: infinityLoad.value
      ? [
          ...(data.value?.products || []),
          ...input.products
        ]
      : input.products
  }),
  onResponse({ response: { _data: { keywordRedirect } } }) {
    if ($feature.enableSearchHistory)
      addSearch(searchQuery.value)
    if (keywordRedirect?.redirectedUrl)
      navigateTo(removeLocale(keywordRedirect.redirectedUrl), { replace: true, external: /^http/i.test(keywordRedirect.redirectedUrl) })
  }
})

const isRedirecting = computed(() => !!data.value?.keywordRedirect?.redirectedUrl)

const loadMore = () => {
  $gtm.push('plpPage.onViewMore')
  query.start! += query.count!
  infinityLoad.value = true
}

const pending = computed(() => loading.value || productLoading.value || isRedirecting.value)
const isArticlesPage = computed(() => $feature.showArticlesTabOnSRP && route.name?.toString().includes('articles'))

const articlesTitle = computed(
  () => articleData.value?.numFound === 0
    ? replaceAll($t.searchResultsZero, { query: searchQuery.value })
    : replaceAll(
        $pluralize($t.searchResultsTitle, articleData.value?.numFound || 0),
        { query: searchQuery.value, count: articleData.value?.numFound || 0 }
      )
)

const productsTitle = computed(
  () => (data.value?.autoCorrectQuery || !data.value?.total)
    ? replaceAll($t.searchResultsZero, { query: query.q })
    : replaceAll(
        $pluralize($t.searchResultsTitle, data.value?.total),
        { query: query.q, count: data.value?.total }
      )
)

const numResults = computed(() =>
  (isArticlesPage.value ? articleData.value?.numFound : data.value?.total) || 0
)

const noResultMessage = computed(() =>
  !hideDidYouMean && data.value?.did_you_mean?.length
    ? $t.didYouMean
    : !hideSubtitle && data.value?.autoCorrectQuery
        ? $t.searchResultsSubtitle
        : null
)

const clear = () => {
  searchFieldQuery.value = ''
  searchFieldRef.value?.$el.focus()
}

const eSpots = $feature.enableSRPESpots ? (await getSections.search()).data.value?.content.sectionsMap : {}
const aboveGridESpots = computed(() => numResults.value > 0 ? eSpots?.['above-grid'] : eSpots?.['above-grid-no-results'])
const aboveFooterESpots = computed(() => numResults.value > 0 ? eSpots?.['lazy-above-footer'] : eSpots?.['lazy-above-footer-no-results'])

const trackingCategory = computed(() => `Search: ${data.value?.autoCorrectQuery ? 'Auto-correction' : 'Standard'}`)

useSeoMeta({
  title: () => replaceAll(
    $t.searchResultsPageTitle,
    { query: data.value?.autoCorrectQuery || searchQuery.value }
  )
})

const pushImpressions = ({ allProductImpressions = false }: { allProductImpressions?: boolean } = {}) => {
  if (!data.value?.products?.length) return

  const cols = { ...grid.cols, lg: $feature.showSideFiltersOnSRP ? grid.cols.lg - 1 : grid.cols.lg }
  let impressedProducts = data.value.products.map((product, index) => ({ ...product, index })) || []

  if (!allProductImpressions)
    impressedProducts = impressedProducts.slice(-data.value.items)

  const chunkSize = 24
  chunkArray(impressedProducts, chunkSize)
    .forEach((productsChunk, i) => {
      $gtm.push('product.onProductImpression', productsChunk, route.query.filters?.toString() || '', {
        sortOption: route.query.sort?.toString() || defaultSort,
        layout: cols[$viewport.breakpoint],
        searchTerm: query.q,
        category: trackingCategory.value,
        chunkId: i,
        offset: (query.start || 0) + i * chunkSize
      })
    })
}

const pushAllGtmEvents = async ({ allProductImpressions = false }: { allProductImpressions?: boolean } = {}) => {
  // Whether the data is present immediately (SSR) or not (CSR), we check for its value
  await until(() => data.value).toBeTruthy()

  const searchTermAdj = data.value?.autoCorrectQuery || data.value?.did_you_mean?.[0] || ''
  const searchType = history.state.searchType

  $gtm.push('page.onLoadPageData', route, {
    pageTitle: await getPageTitle(),
    searchTerm: router.currentRoute.value.query.q as string || '',
    searchResults: data.value?.total || 0,
    searchTermAdj,
    searchType: searchType || (searchTermAdj ? 'Suggested search results' : 'Standard'),
    sourceId: data.value?.responseId
  })
  $gtm.push('user.onLoadUserData', await getUserEventData())
  $gtm.push('page.onRouterChange', route)
  $gtm.push('cart.onCartLoad', cart)
  if (!isArticlesPage.value) pushImpressions({ allProductImpressions })
}

onMounted(() => {
  history?.replaceState({
    category: trackingCategory.value,
    searchTerm: searchQuery.value,
    searchType: history.state.searchType
  }, '')
  pushAllGtmEvents()
  injectFitFinder()
  setInteractionOrigin()
  trackEvent('search', { searchTerm: searchQuery.value })
})

onActivated(async () => {
  // Push impressions after restoring from keep-alive cache
  await pushAllGtmEvents({ allProductImpressions: true })
  injectFitFinder()
})

onBeforeUnmount(() => {
  data.value = null
})

watch(() => [searchQuery.value, sort.value, storeFilter?.value, filters.value], (newValue, oldValue) => {
  if (newValue[0] !== oldValue?.[0]) return
  if (newValue.every((v, i) => v === oldValue?.[i])) return
  infinityLoad.value = false
  query.start = 0
  query.sort = sort.value
  query.filters = filtersQuery.value
  query.storeFilter = toValue(storeFilter)
})

watchImmediate(searchQuery, () => {
  if (isClient() && $dtrum) {
    const actionId = $dtrum.enterAction('Searched')
    $dtrum.addActionProperties(actionId, null, null, {
      searchTerm: searchQuery.value
    })
    $dtrum.leaveAction(actionId)
  }
})

watch(data, (_, oldValue) => {
  // Push new impressions on update of page, filters, sort, etc.
  if (oldValue) { // Skip the push on initial page load - it will be handled by onMounted -> pushAllGtmEvents
    pushImpressions()
  }
})
</script>
